/**
 * -----------------------------------------------------------------------------
 * Popup - Create New Driver
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new driver in the organization.
 */

import { Formik, useFormik } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCreateOrganizationDriverMutation } from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { TModalsState, setModalsData } from "../../../api/store/commonSlice";
import { AdminFormFieldInput, AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserData } from "../../../utils/user";
import { formInitialValues, TFormFieldNames, YupValidationSchema } from "./validation";
import FTModal from "../../../components/common/FTModal";
import FormActionButtons from "../../../components/admin/formActionButtons";

const AdminsDriversCreateNew = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation('translation', { keyPrefix: 'admins.drivers.create_new' });
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");

  // driver mutation
  const [createOrgDriverAPITrigger] = useCreateOrganizationDriverMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showCreateDriver: false }));
  };
    const formik = useFormik({
        initialValues:formInitialValues,
        validationSchema:YupValidationSchema,
        onSubmit:(values, { setSubmitting }) => {
          createOrgDriverAPITrigger({
            accountid: `${thisUserOrganizationId}`,
            driverid: values.name.replace(/\s+/g, ''),
            isactive: 1,
            licensestatus: 'N/A', // TODO: [P] need to confirm it is mandatory or not
            displayname: values.name,
            contactemail: values.email,
            contactphone: values.phone,
          })
            .unwrap()
            .then((data) => {
              if (!!data?.driverid) { // changes the checkcase condition
                dispatch(setModalsData({ ...modalsState, showCreateDriver: false }));
                toast.success(t('toast.create_success'), { autoClose: 10000 });
                navigate(`${routeUrls.dashboardChildren.adminChildren.drivers}/${data?.driverid}`, { state: { new: true } });
              } else {
                toast.error(t('toast.create_failed'));
              }
            })
            .catch((error) => {
              const errors = !!error?.data ? serializeErrorKeyValues(error?.data) : [t('toast.create_failed')];
              // toast.error(errors?.join(' '));
              //if in error message Duplicate string is found then show the message as "Driver ID already exists"
              if (errors[0].includes('Duplicate')) {
                toast.error(t('toast.create_duplicate_failed'));
              } else {
                toast.error(errors?.join(' '));
              }
            })
            .finally(() => {
              setSubmitting(false);
            });
        }
      });
      const{
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        dirty,
        setFieldValue,
      } = formik;
        const invalidFields =
          Object.keys(errors)
            .filter(key => !!errors[key as TFormFieldNames]);
        const isFormValid = invalidFields.length === 0 && dirty;
        const MemoizedFormActionButtons = useMemo(() => {
          return (
            <>
              <FormActionButtons
                buttons={[
                  {
                    label:t('submit_button'),
                    variant:"primary",
                    disabled:!isFormValid || isSubmitting /* || isLoading*/
                  },
                  {
                    label:tMain('cancel'),
                    type:"button",
                    variant:"danger-transparent",
                    disabled:isSubmitting , /* || isLoading*/
                    onClick:()=>hideModal()
                  },
                ]}
              />
            </>
          );
        }, [
          isFormValid,
          isSubmitting,    
        ])

  return (
    <FTModal
      visible={modalsState.showCreateDriver}
      heading={t("heading")}
      onHide={hideModal}
    >
          <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
            <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
              {t("sub_heading")}
            </p>

            <AdminFormFieldInput
              label={t('name')}
              type="text"
              id="Name"
              name="name"
              placeholder={t('name_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              touched={touched.name}
              error={errors.name} />

            <AdminFormFieldInput
              label={t('email')}
              type="email"
              id="Email"
              name="email"
              placeholder={t('email_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              touched={touched.email}
              error={errors.email} />

            <AdminFormFieldInput
              label={t('phone')}
              type="text"
              id="Phone"
              name="phone"
              placeholder={t('phone_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.phone}
              touched={touched.phone}
              error={errors.phone} />
            {MemoizedFormActionButtons}
            {/* <AdminFormFieldSubmit
              label={t('submit_button')}
              variant="primary"
              disabled={!isFormValid || isSubmitting } // || isLoading
            />

            <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting } // || isLoading
              onClick={hideModal}
            /> */}
          </form>
    </FTModal>
  )
};
export default AdminsDriversCreateNew;